import React, { useState, useEffect } from 'react';
import { useRef } from "react";
import { useReactToPrint } from 'react-to-print';
import './Purchases.css';
import api from '../Service/api';
import UniversalInvoicePrint from './UniversalInvoicePrint';

function Purchases() {
  const fileInputRef = useRef(null);

  const [invoiceNumber, setInvoiceNumber] = useState(0);
  const [vendorName, setVendorName] = useState("");
  const [rent, setRent] = useState("");
  const [generalDiscount, setGeneralDiscount] = useState(0);
  const [vendorList, setVendorList] = useState([]);
  const [description, setDescription] = useState("");
  const [amountPaid, setAmountPaid] = useState(0);
  const [gstPercentage, setGstPercentage] = useState("");
  const [selectedVendorBalance, setSelectedVendorBalance] = useState(null);
  const [searchTerms, setSearchTerms] = useState({});
  const [sizeFilters, setSizeFilters] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);

  const [invoiceDate, setInvoiceDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  const [printInvoice, setPrintInvoice] = useState(null);
  const [invoiceImage, setInvoiceImage] = useState(null);

  const [products, setProducts] = useState([{
    productName: '', category: '', quantity: '', price: '',
    size: '', ktae: '', gram: '', boxSize: '', particular: '', length: ''
  }]);
  const [productList, setProductList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);

  const printRef = useRef();
  const [canPrint, setCanPrint] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [needsScroll, setNeedsScroll] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      const table = document.querySelector('.purchase-table');
      if (table) {
        const needsHorizontalScroll = table.scrollWidth > table.clientWidth;
        setNeedsScroll(needsHorizontalScroll);
      }
    };
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [products]);

  useEffect(() => {
    const phone = localStorage.getItem("userPhone");
    if (!phone) return;
    api.get(`/authentication/userData/${phone}`)
      .then(res => setLoggedInUser(res.data.additionalDetail))
      .catch(err => console.error(err));
  }, []);

  const refreshVendorList = async () => {
    try {
      const res = await api.get("/product/getAll");
      if (res.data.code === 1) setVendorList(res.data.additionalDetail);
    } catch (err) {
      console.error("Error refreshing vendor list", err);
    }
  };

  const getFilteredProductsForRow = (rowIndex, searchTerm, sizeFilter) => {
    let filtered = [...productList];
    
    // Apply size filter if present
    const sizeStr = sizeFilter ? String(sizeFilter).trim() : '';
    
    if (sizeStr !== '') {
      const sizeNum = parseFloat(sizeStr);
      if (!isNaN(sizeNum)) {
        filtered = filtered.filter(p => {
          const cat = (p.category || "").toLowerCase();
          // For roll products
          if (cat === 'roll') {
            return p.size && parseFloat(p.size) === sizeNum;
          }
          // For reel/tota products - check stockByWeight keys for size
          if (cat === 'reel' || cat === 'tota') {
            if (p.stockByWeight) {
              return Object.keys(p.stockByWeight).some(key => {
                const match = key.match(/^(\d+(?:\.\d+)?)/);
                return match && parseFloat(match[1]) === sizeNum;
              });
            }
            return false;
          }
          // For packet products
          if (cat === 'packet') {
            return p.size && parseFloat(p.size) === sizeNum;
          }
          return true;
        });
      }
    }
    
    // Apply search filter
    if (searchTerm && searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(p => 
        p.productName.toLowerCase().includes(term)
      );
    }
    
    // Limit results
    return filtered.slice(0, 25);
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Invoice-${invoiceNumber}`,
  });

  const totalQuantity = products.reduce((sum, p) => {
    const qty = parseFloat(p.quantity);
    return sum + (isNaN(qty) ? 0 : qty);
  }, 0);

  const handlePrintClick = () => {
    if (!printInvoice) {
      alert("No invoice data to print");
      return;
    }
    handlePrint();
  };

  const fetchAllProducts = async () => {
    setLoadingProducts(true);
    try {
      let allProducts = [];
      let currentPage = 0;
      const pageSize = 100;
      let hasMore = true;
      while (hasMore) {
        const res = await api.get("/product/names-prices", {
          params: { page: currentPage, size: pageSize , _: Date.now()}
        });
        if (res.data.code === 1) {
          const products = res.data.additionalDetail;
          if (products && products.length > 0) {
            allProducts = [...allProducts, ...products];
            currentPage++;
            hasMore = products.length === pageSize;
          } else hasMore = false;
        } else hasMore = false;
      }
      setProductList(allProducts);
      console.log("🔄 Product list updated with", allProducts.length, "items");

      return allProducts;
    } catch (err) {
      console.error(err);
      return [];
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchAllCategories = async () => {
    setLoadingCategories(true);
    try {
      let allCategories = [];
      let currentPage = 0;
      const pageSize = 100;
      let hasMore = true;
      while (hasMore) {
        const res = await api.get("/api/category/getCategories", {
          params: { page: currentPage, size: pageSize }
        });
        if (res.data.code === 1) {
          const categories = res.data.additionalDetail;
          if (categories && categories.length > 0) {
            allCategories = [...allCategories, ...categories];
            currentPage++;
            hasMore = categories.length === pageSize;
          } else hasMore = false;
        } else hasMore = false;
      }
      setCategoryList(allCategories);
      return allCategories;
    } catch (err) {
      console.error(err);
      return [];
    } finally {
      setLoadingCategories(false);
    }
  };

  const refreshProductList = async () => {
    await fetchAllProducts();
  };

  useEffect(() => {
    fetchAllProducts();
  }, []);

  useEffect(() => {
    fetchAllCategories();
  }, []);

  useEffect(() => {
    const fetchVendors = async () => {
      setLoadingVendors(true);
      try {
        let allVendors = [];
        let currentPage = 0;
        const pageSize = 100;
        let hasMore = true;
        while (hasMore) {
          const res = await api.get("/product/getAll", {
            params: { page: currentPage, size: pageSize }
          });
          if (res.data.code === 1) {
            const vendors = res.data.additionalDetail;
            if (vendors && vendors.length > 0) {
              allVendors = [...allVendors, ...vendors];
              currentPage++;
              hasMore = vendors.length === pageSize;
            } else hasMore = false;
          } else hasMore = false;
        }
        setVendorList(allVendors);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingVendors(false);
      }
    };
    fetchVendors();
  }, []);

  useEffect(() => {
  const handleWheel = (e) => {
    if (e.target.type === 'number') {
      e.preventDefault();
    }
  };
  const container = document.querySelector('.purchase-table-container');
  if (container) {
    container.addEventListener('wheel', handleWheel, { passive: false });
  }
  return () => {
    if (container) {
      container.removeEventListener('wheel', handleWheel);
    }
  };
}, []);

  const handleVendorChange = (e) => {
    const name = e.target.value;
    setVendorName(name);
    const vendor = vendorList.find(v => v.vendorName === name);
    if (vendor && vendor.currentBalance !== undefined) {
      setSelectedVendorBalance(vendor.currentBalance);
    } else {
      setSelectedVendorBalance(null);
    }
  };

  // Filter stock based on entered size/length
  const getFilteredStock = (stockByWeight, category, sizeValue, lengthValue) => {
    if (!stockByWeight) return null;
    
    const filtered = {};
    const lowerCat = category?.toLowerCase();
    
    for (const [display, qty] of Object.entries(stockByWeight)) {
      if (lowerCat === 'roll') {
        if (sizeValue) {
          const displaySize = display.match(/^(\d+(?:\.\d+)?)/);
          if (displaySize && parseFloat(displaySize[1]) === parseFloat(sizeValue)) {
            filtered[display] = qty;
          }
        } else {
          filtered[display] = qty;
        }
      } else if (lowerCat === 'reel' || lowerCat === 'tota') {
        if (lengthValue) {
          const lengthMatch = display.match(/\|\s*(\d+(?:\.\d+)?)m/);
          if (lengthMatch && parseFloat(lengthMatch[1]) === parseFloat(lengthValue)) {
            filtered[display] = qty;
          }
        } else {
          filtered[display] = qty;
        }
      } else {
        filtered[display] = qty;
      }
    }
    
    return filtered;
  };

  const removeRow = (index) => {
  // Remove the product
  setProducts(prev => prev.filter((_, i) => i !== index));

  // Re‑index searchTerms and sizeFilters to keep them aligned
  const newSearchTerms = {};
  const newSizeFilters = {};

  Object.keys(searchTerms).forEach(key => {
    const k = parseInt(key, 10);
    if (k < index) newSearchTerms[k] = searchTerms[k];
    else if (k > index) newSearchTerms[k - 1] = searchTerms[k];
  });

  Object.keys(sizeFilters).forEach(key => {
    const k = parseInt(key, 10);
    if (k < index) newSizeFilters[k] = sizeFilters[k];
    else if (k > index) newSizeFilters[k - 1] = sizeFilters[k];
  });

  setSearchTerms(newSearchTerms);
  setSizeFilters(newSizeFilters);
};

  const handleChange = (index, field, value) => {
    const newProducts = [...products];
    newProducts[index][field] = value;

    // Update size filter when size field changes
    if (field === "size") {
      setSizeFilters(prev => ({
        ...prev,
        [index]: value !== undefined && value !== null ? String(value) : ''
      }));
    }

    // Update length filter too (for reel/tota)
    if (field === "length") {
      setSizeFilters(prev => ({
        ...prev,
        [index]: value !== undefined && value !== null ? String(value) : ''
      }));
    }

    if (field === "productName") {
      setSearchTerms(prev => ({
        ...prev,
        [index]: value
      }));
      const foundProduct = productList.find(p => p.productName.toLowerCase() === value.toLowerCase());
      if (foundProduct) {
        newProducts[index].price = foundProduct.purchasePrice;
        newProducts[index].category = foundProduct.category;
        const categoryName = (foundProduct.category || "").toLowerCase();
        if (categoryName === 'roll' && foundProduct.size) {
          newProducts[index].size = foundProduct.size;
        }
      }
    }

    const category = (newProducts[index].category || "").toLowerCase();
    if (field === "quantity" && (category === "reel" || category === "tota")) {
      const weightValue = parseFloat(value) || 0;
      newProducts[index].size = weightValue;
    }

    if (field === "category") {
      const foundCategory = categoryList.find(c => c.categoryName.toLowerCase() === value.toLowerCase());
      newProducts[index].category = foundCategory ? foundCategory.categoryName : value;

      const newCat = value.toLowerCase();
      if (newCat !== 'reel' && newCat !== 'tota') {
        newProducts[index].length = '';
      }
    }

    if (field === "category") {
      const newCategory = value.toLowerCase();
      if (newCategory !== 'box') {
        newProducts[index].boxSize = '';
        newProducts[index].particular = '';
      }
      if (newCategory !== 'packet') {
        newProducts[index].ktae = '';
        newProducts[index].gram = '';
      }
      if (!['packet', 'roll', 'reel', 'tota', 'box'].includes(newCategory)) {
        newProducts[index].size = '';
      }
    }

    const currentCategory = (newProducts[index].category || "").toLowerCase();
    const price = parseFloat(newProducts[index].price) || 0;
    const quantity = parseFloat(newProducts[index].quantity) || 0;
    const size = parseFloat(newProducts[index].size) || 0;
    const ktae = parseFloat(newProducts[index].ktae) || 0;
    const gram = parseFloat(newProducts[index].gram) || 0;

    if (currentCategory === 'packet') {
      newProducts[index].calculatedTotal = ((quantity * size * ktae * gram * price) / 15500).toFixed(2);
    } else if (currentCategory === 'roll') {
      newProducts[index].calculatedTotal = (price * size * quantity).toFixed(2);
    } else if (currentCategory === "reel" || currentCategory === "tota") {
      newProducts[index].calculatedTotal = (price * quantity).toFixed(2);
    } else if (currentCategory === 'box') {
      newProducts[index].calculatedTotal = (price * quantity).toFixed(2);
    } else {
      newProducts[index].calculatedTotal = (price * quantity).toFixed(2);
    }

    setProducts(newProducts);
  };

  const addRow = () => {
    setProducts([...products, {
      productName: '', category: '', quantity: '', price: '',
      size: '', ktae: '', gram: '', boxSize: '', particular: '', length: ''
    }]);
  };

  const removeAllRows = () => {
    setProducts([]);
    setGstPercentage("");
  };

  const handleFileChange = (e) => {
    setInvoiceImage(e.target.files[0]);
  };

  const validateForm = () => {
    if (!vendorName || vendorName.trim() === "") {
      alert("Vendor name is required");
      return false;
    }

    if (!products || products.length === 0) {
      alert("At least one product is required");
      return false;
    }

    for (let i = 0; i < products.length; i++) {
      const p = products[i];
      const row = i + 1;

      if (!p.productName || p.productName.trim() === "") {
        alert(`Product name is required (Row ${row})`);
        return false;
      }

      if (!p.category || p.category.trim() === "") {
        alert(`Category is required (Row ${row})`);
        return false;
      }

      const category = p.category.toLowerCase();

      if (category === "reel" || category === "tota") {
        if (!p.quantity || Number(p.quantity) <= 0) {
          alert(`Weight (Quantity) is required for ${category} (Row ${row})`);
          return false;
        }
        if (!p.length || Number(p.length) <= 0) {
          alert(`Length is required for ${category} (Row ${row})`);
          return false;
        }
      }

      if (category === "packet") {
        if (!p.size || Number(p.size) <= 0) {
          alert(`Size is required for packet (Row ${row})`);
          return false;
        }
        if (!p.ktae || Number(p.ktae) <= 0) {
          alert(`Ktae is required for packet (Row ${row})`);
          return false;
        }
        if (!p.gram || Number(p.gram) <= 0) {
          alert(`Gram is required for packet (Row ${row})`);
          return false;
        }
      }

      if (category === "roll") {
        if (!p.size || Number(p.size) <= 0) {
          alert(`Size is required for ${category} (Row ${row})`);
          return false;
        }
      }

      if (category === "box") {
        if (!p.boxSize || p.boxSize.trim() === "") {
          alert(`Box Size is required for box (Row ${row})`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async () => {
  if (!validateForm()) return;
  setIsSubmitting(true);

  try {
    const formData = new FormData();

    const requestBody = {
      products: products.map(p => {
        const category = p.category.toLowerCase();
        const baseProduct = {
          productName: p.productName,
          category: p.category,
          quantity: parseFloat(p.quantity) || 0,
          price: parseFloat(p.price) || 0,
          // REMOVED: invoiceNumber - Backend will generate it
        };

        if (category === 'packet') {
          return {
            ...baseProduct,
            size: parseFloat(p.size) || 0,
            ktae: parseFloat(p.ktae) || 0,
            gram: parseFloat(p.gram) || 0,
            boxSize: null,
            particular: null,
            length: 0
          };
        } else if (category === 'roll') {
          return {
            ...baseProduct,
            size: parseFloat(p.size) || 0,
            ktae: null,
            gram: null,
            boxSize: null,
            particular: null,
            length: 0
          };
        } else if (category === 'reel' || category === 'tota') {
          return {
            ...baseProduct,
            size: parseFloat(p.quantity) || 0,
            length: parseFloat(p.length) || 0,
            ktae: null,
            gram: null,
            boxSize: null,
            particular: p.particular || ""
          };
        } else if (category === 'box') {
          return {
            ...baseProduct,
            size: 0,
            ktae: null,
            gram: null,
            boxSize: p.boxSize || "",
            particular: p.particular || "",
            length: 0
          };
        } else {
          return {
            ...baseProduct,
            size: 0,
            ktae: null,
            gram: null,
            boxSize: null,
            particular: p.particular || "",
            length: 0
          };
        }
      }),
      invoiceDiscount: parseFloat(generalDiscount) || 0,
      invoiceRent: parseFloat(rent) || 0,
      description,
      vendorName,
      amountPaid: parseFloat(amountPaid) || 0,
      gstPercentage: gstPercentage ? parseFloat(gstPercentage) : null,
      purchaseDate: invoiceDate
    };

    formData.append("request", JSON.stringify(requestBody));
    formData.append("status", "Purchase");

    if (invoiceImage) {
      formData.append("invoiceImage", invoiceImage);
    }

    const response = await api.post("/product/stockAdded", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });

    // ===== GET INVOICE NUMBER FROM returnId =====
    const generatedInvoiceNumber = response.data.returnId || invoiceNumber || 0;
    console.log("📄 Generated Purchase Invoice Number:", generatedInvoiceNumber);
    
    // Update state with actual invoice number from backend
    setInvoiceNumber(generatedInvoiceNumber);
    // ===========================================

    if (response.data.code === 1) {
      alert(response.data.message || "Saved!");
    } else {
      alert(response.data.additionalDetail || "Failed to save");
      setIsSubmitting(false);
      return;
    }

    setCanPrint(true);
    setInvoiceImage(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    const invoiceSnapshot = {
      invoiceNumber: generatedInvoiceNumber,  // Use the generated number
      vendorName,
      products: JSON.parse(JSON.stringify(products)),
      invoiceDiscount: generalDiscount,
      invoiceRent: rent,
      payBill: amountPaid,
      gstPercentage: gstPercentage || 0,
      grandTotal,
      user: loggedInUser,
      invoiceDate: invoiceDate
    };
    setPrintInvoice(invoiceSnapshot);

    // Reset form fields
    setProducts([{
      productName: '', category: '', quantity: '', price: '',
      size: '', ktae: '', gram: '', boxSize: '', particular: '', length: ''
    }]);
    setVendorName("");
    setSelectedVendorBalance(null);
    setRent("");
    setGeneralDiscount(0);
    setGstPercentage("");
    setDescription("");
    setAmountPaid(0);
    setInvoiceDate(new Date().toISOString().split('T')[0]);

    // REMOVED: Don't fetch next invoice number from backend
    // The backend will handle the next number generation

    try {
      await refreshProductList();
      await refreshVendorList();
    } catch (refreshErr) {
      console.error("Error refreshing product list:", refreshErr);
    }

    setIsSubmitting(false);
  } catch (error) {
    console.error(error);
    alert("Failed to save.");
    setIsSubmitting(false);
  }
};

  let grandTotal = products.reduce((sum, p) => sum + (parseFloat(p.calculatedTotal) || 0), 0);
  if (rent) grandTotal += parseFloat(rent);
  if (generalDiscount) grandTotal -= parseFloat(generalDiscount);
  let gstAmount = 0;
  let subtotalBeforeGst = grandTotal;
  if (gstPercentage && parseFloat(gstPercentage) > 0) {
    gstAmount = grandTotal * parseFloat(gstPercentage) / 100;
    grandTotal += gstAmount;
  }
  if (amountPaid != null && amountPaid !== "") {
    grandTotal -= parseFloat(amountPaid) || 0;
  }
  grandTotal = grandTotal.toFixed(2);

  const categories = products.map(p => (p.category || "").toLowerCase());
  const showPacketColumns = categories.includes("packet");
  const showRollColumns = categories.some(c => ["roll"].includes(c));
  const showBoxColumns = categories.includes("box");
  const showParticularColumn = categories.some(c => c !== 'roll' && c !== 'packet');
  const showWeightLabel = products.some(p => {
    const cat = (p.category || "").toLowerCase();
    return cat === "reel" || cat === "tota";
  });
  const showLengthColumn = products.some(p => {
    const cat = (p.category || "").toLowerCase();
    return cat === "reel" || cat === "tota";
  });
  // Show Size column if ANY product needs it (packet, roll, or reel/tota)
  const showSizeColumn = showPacketColumns || showRollColumns || showLengthColumn;

  return (
    <div className={`purchase-table-container ${needsScroll ? 'has-scroll' : ''}`}>
      <h2 className="page-title" style={{ color: "#0a8938" }}>Purchase Details</h2>

      {needsScroll && (
        <div className="scroll-hint">↔ Scroll horizontally to see all columns</div>
      )}

      {(loadingProducts || loadingCategories || loadingVendors) && (
        <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#e3f2fd', marginBottom: '10px', borderRadius: '4px' }}>
          ⏳ Loading data... {loadingProducts && "Products "} {loadingCategories && "Categories "} {loadingVendors && "Vendors "}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px", marginBottom: "20px" }}>
<div><label style={{ fontWeight: "bold" }}>Invoice #</label>
  <input type="text" value={invoiceNumber || "Auto-generated"} readOnly className="read-only-input" />
</div>        <div><label style={{ fontWeight: "bold" }}>Purchase Date</label><input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} className="read-only-input" max={new Date().toISOString().split('T')[0]} /></div>
        <div>
          <label style={{ fontWeight: "bold" }}>Vendor Name</label>
          <input type="text" list="vendorNames" value={vendorName} onChange={handleVendorChange} placeholder="Enter Vendor Name" className="read-only-input" />
          <datalist id="vendorNames">
            {vendorList.map((v, i) => (
              <option key={i} value={v.vendorName}>
                {v.vendorName} {v.currentBalance !== undefined ? `(Balance: ${v.currentBalance})` : ''}
              </option>
            ))}
          </datalist>
          {selectedVendorBalance !== null && (
            <div style={{ fontSize: '12px', color: '#0a8938', marginTop: '5px' }}>
              Current Balance: Rs {selectedVendorBalance.toFixed(2)}
            </div>
          )}
        </div>
      </div>

      <div className="table-scroll-wrapper">
        <table className="purchase-table">
          <thead>
            <tr>
              <th>Product Name</th>
              <th>Category</th>
              <th>{showWeightLabel ? "Weight (kg)" : "Quantity"}</th>
              <th>Rate</th>
              {/* SINGLE Size column - shown if packet, roll, or reel/tota products exist */}
              {showSizeColumn && <th>Size</th>}
              {showPacketColumns && (<><th>Ktae</th><th>Gram</th></>)}
              {showBoxColumns && <th>Box Size</th>}
              {showParticularColumn && <th>Particular</th>}
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, idx) => {
              const category = product.category.toLowerCase();
              const selectedProduct = productList.find(p => p.productName === product.productName);
              console.log("🔍 selectedProduct for", product.productName, selectedProduct);

              const isReelOrTota = category === 'reel' || category === 'tota';
              const isRoll = category === 'roll';
              const isPacket = category === 'packet';
              
              // Get filtered stock for display
              const filteredStock = selectedProduct && (isReelOrTota || isRoll) 
                ? getFilteredStock(selectedProduct.stockByWeight, product.category, product.size, product.length)
                : null;
              
              const hasStock = filteredStock && Object.values(filteredStock).some(qty => qty > 0);
              
              return (
                <tr key={idx}>
                  <td style={{ position: 'relative', minWidth: '150px', maxWidth: '250px' }}>
                    <input
                      list={`products-${idx}`}
                      value={product.productName}
                      onChange={e => {
                        handleChange(idx, "productName", e.target.value);
                        setSearchTerms(prev => ({
                          ...prev,
                          [idx]: e.target.value
                        }));
                      }}
                      onFocus={() => {
                        setSearchTerms(prev => ({
                          ...prev,
                          [idx]: product.productName || ''
                        }));
                      }}
                      className="read-only-input"
                      placeholder='Search Product...'
                      style={{
                        borderColor: (() => {
                          if (!selectedProduct) return '';
                          const cat = (selectedProduct.category || "").toLowerCase();
                          if ((cat === 'reel' || cat === 'tota' || cat === 'roll') && selectedProduct.stockByWeight) {
                            const sizeVal = product.size || product.length;
                            if (sizeVal) {
                              const hasStock = Object.entries(selectedProduct.stockByWeight).some(([key, qty]) => {
                                const match = key.match(/^(\d+(?:\.\d+)?)/);
                                return match && parseFloat(match[1]) === parseFloat(sizeVal) && qty > 0;
                              });
                              return !hasStock ? '#ff4444' : '';
                            }
                            const hasStock = Object.values(selectedProduct.stockByWeight).some(qty => qty > 0);
                            return !hasStock && product.productName ? '#ff4444' : '';
                          }
                          return (selectedProduct?.stockQuantity !== undefined && selectedProduct.stockQuantity <= 0 && product.productName) ? '#ff4444' : '';
                        })(),
                        backgroundColor: (() => {
                          if (!selectedProduct) return '';
                          const cat = (selectedProduct.category || "").toLowerCase();
                          if ((cat === 'reel' || cat === 'tota' || cat === 'roll') && selectedProduct.stockByWeight) {
                            const sizeVal = product.size || product.length;
                            if (sizeVal) {
                              const hasStock = Object.entries(selectedProduct.stockByWeight).some(([key, qty]) => {
                                const match = key.match(/^(\d+(?:\.\d+)?)/);
                                return match && parseFloat(match[1]) === parseFloat(sizeVal) && qty > 0;
                              });
                              return !hasStock ? '#fff0f0' : '';
                            }
                            const hasStock = Object.values(selectedProduct.stockByWeight).some(qty => qty > 0);
                            return !hasStock && product.productName ? '#fff0f0' : '';
                          }
                          return (selectedProduct?.stockQuantity !== undefined && selectedProduct.stockQuantity <= 0 && product.productName) ? '#fff0f0' : '';
                        })()
                      }}
                    />
                    
                    <datalist id={`products-${idx}`}>
                      {getFilteredProductsForRow(idx, searchTerms[idx] || '', product.size || product.length || '').map((p, i) => {
                        const cat = (p.category || "").toLowerCase();
                        const isReelTotaRoll = cat === 'reel' || cat === 'tota' || cat === 'roll';
                        const isPacket = cat === 'packet';
                        
                        let displayText = p.productName;
                        
                        if (isReelTotaRoll && p.stockByWeight) {
                          const sizeVal = product.size || product.length;
                          if (sizeVal) {
                            const matchingStock = Object.entries(p.stockByWeight).filter(([key, qty]) => {
                              const match = key.match(/^(\d+(?:\.\d+)?)/);
                              return match && parseFloat(match[1]) === parseFloat(sizeVal) && qty > 0;
                            });
                            if (matchingStock.length > 0) {
                              displayText += ` [${matchingStock.map(([key, qty]) => `${key}: ${qty}`).join(', ')}]`;
                            } else {
                              displayText += ' (No stock for this size)';
                            }
                          } else {
                            const nonZero = Object.entries(p.stockByWeight).filter(([_, qty]) => qty > 0);
                            displayText += nonZero.length > 0 ? ` (${nonZero.length} sizes)` : ' (Out)';
                          }
                        } else if (isPacket && p.packetStockByVariant) {
                          const nonZero = Object.entries(p.packetStockByVariant).filter(([_, qty]) => qty > 0);
                          displayText += nonZero.length > 0 ? ` (${nonZero.length} variants)` : ' (Out)';
                        } else if (p.stockQuantity !== undefined) {
                          displayText += p.stockQuantity > 0 ? ` (${p.stockQuantity})` : ' (Out)';
                        }
                        
                        return <option key={i} value={p.productName}>{displayText}</option>;
                      })}
                      {getFilteredProductsForRow(idx, searchTerms[idx] || '', product.size || product.length || '').length === 0 && (
                        <option value="" disabled>
                          {searchTerms[idx] || product.size || product.length 
                            ? 'No products found for this size' 
                            : 'No products available'}
                        </option>
                      )}
                    </datalist>

                    {/* Stock Display */}
  {selectedProduct && product.productName && (
  <div style={{
    marginTop: '6px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px 8px',
    fontSize: '13px',
    color: '#333',
    borderTop: '1px dashed #ddd',
    paddingTop: '4px'
  }}>
    {/* Stock icon and label */}
    <span style={{ fontWeight: '600', color: '#0a8938', marginRight: '4px' }}>
      📦 Stock:
    </span>

    {(() => {
      // Collect all stock entries with quantity > 0
      let entries = [];
      if (filteredStock && Object.keys(filteredStock).length > 0) {
        entries = Object.entries(filteredStock).filter(([_, qty]) => qty > 0);
      } else if (selectedProduct.stockQuantity !== undefined && selectedProduct.stockQuantity > 0) {
        entries = [['Available', selectedProduct.stockQuantity]];
      }

      if (entries.length === 0) {
        return <span style={{ color: '#ff4444', fontWeight: '500' }}>Out of Stock</span>;
      }

      return entries.map(([display, qty]) => (
        <span key={display} style={{
          background: '#e8f5e9',
          padding: '2px 10px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '500',
          color: '#1e7e34',
          whiteSpace: 'nowrap',
          border: '1px solid #a5d6a7'
        }}>
          {display}: <strong>{qty}</strong>
        </span>
      ));
    })()}
  </div>
)}
                    {/* Size Filter Indicator */}
                    {(product.size || product.length) && (
                      <div style={{ 
                        fontSize: '10px', 
                        color: '#3b82f6', 
                        marginTop: '4px',
                        padding: '2px 6px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span>🔍 Filter: {product.size || product.length}</span>
                        <button 
                          onClick={() => {
                            setSizeFilters(prev => ({...prev, [idx]: ''}));
                            const newProducts = [...products];
                            newProducts[idx].size = '';
                            newProducts[idx].length = '';
                            setProducts(newProducts);
                          }}
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            color: '#ff4444', 
                            cursor: 'pointer', 
                            fontSize: '12px',
                            fontWeight: 'bold',
                            padding: '0 4px'
                          }}
                          title="Clear size filter"
                        >
                          ✕
                        </button>
                      </div>
                    )}

                    {(isReelOrTota) && (!product.quantity || parseFloat(product.quantity) <= 0) && product.productName && (
                      <div style={{ fontSize: '11px', color: '#ff9800', marginTop: '2px', fontWeight: '500' }}>⚡ Enter weight in kg in the Quantity column</div>
                    )}
                    {isRoll && (!product.quantity || parseFloat(product.quantity) <= 0) && product.productName && (
                      <div style={{ fontSize: '11px', color: '#ff9800', marginTop: '2px', fontWeight: '500' }}>⚡ Enter quantity (rolls) in the Quantity column</div>
                    )}
                    {isRoll && !product.size && product.productName && (
                      <div style={{ fontSize: '11px', color: '#ff4444', marginTop: '2px', fontWeight: '500' }}>⚠️ Size is required for roll products</div>
                    )}
                    {isReelOrTota && (!product.length || parseFloat(product.length) <= 0) && product.productName && (
                      <div style={{ fontSize: '11px', color: '#ff9800', marginTop: '2px', fontWeight: '500' }}>⚡ Size is required</div>
                    )}
                  </td>

                  <td>
                    <input list={`categories-${idx}`} value={product.category} onChange={e => handleChange(idx, "category", e.target.value)} className="read-only-input" />
                    <datalist id={`categories-${idx}`}>
                      {categoryList.map((c, i) => <option key={i} value={c.categoryName} />)}
                    </datalist>
                  </td>

                  <td>
                    <input
                      type="number"
                      value={product.quantity}
                      onChange={e => handleChange(idx, "quantity", e.target.value)}
                      className="read-only-input"
                      placeholder={isReelOrTota ? "Weight in kg" : (isRoll ? "Quantity (rolls)" : "Quantity")}
                      step={isReelOrTota ? "1" : "0.01"}
                    />
                  </td>

                  <td>
                    <input type="number" value={product.price} onChange={e => handleChange(idx, "price", e.target.value)} className="read-only-input" />
                  </td>

                  {/* SINGLE Size column - shows size for packet/roll and length for reel/tota */}
                  {showSizeColumn && (
                    <td>
                      {isPacket || isRoll ? (
                        <input 
                          type="number" 
                          value={product.size || ""} 
                          onChange={e => handleChange(idx, "size", e.target.value)} 
                          className="read-only-input" 
                          placeholder={isRoll ? "Size (e.g., 18)" : "Size"}
                          style={{ 
                            borderColor: (isRoll && !product.size && product.productName) ? '#ff4444' : '',
                            fontWeight: isRoll ? '500' : 'normal'
                          }}
                        />
                      ) : isReelOrTota ? (
                        <input
                          type="number"
                          step="any"
                          value={product.length || ""}
                          onChange={e => handleChange(idx, "length", e.target.value)}
                          className="read-only-input"
                          placeholder="Length (m)"
                          style={{ 
                            borderColor: (!product.length && product.productName) ? '#ff4444' : '',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />
                      )}
                      {isRoll && !product.size && product.productName && (
                        <div style={{ fontSize: '11px', color: '#ff4444', marginTop: '2px', fontWeight: '500' }}>
                          ⚠️ Size is required for roll products
                        </div>
                      )}
                      {/* Improved Stock Display in Size Column */}
{selectedProduct && selectedProduct.stockByWeight && (
  <div style={{
    marginTop: '6px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
    {(() => {
      // Determine which stock entries to show based on category and size/length
      let entries = [];
      const cat = category;
      if (isReelOrTota && product.length) {
        // For reel/tota: filter by length
        entries = Object.entries(selectedProduct.stockByWeight)
          .filter(([key, qty]) => {
            const match = key.match(/\|\s*(\d+(?:\.\d+)?)m/);
            return match && parseFloat(match[1]) === parseFloat(product.length) && qty > 0;
          });
      } else if (isRoll && product.size) {
        // For roll: filter by size
        entries = Object.entries(selectedProduct.stockByWeight)
          .filter(([key, qty]) => {
            const match = key.match(/^(\d+(?:\.\d+)?)/);
            return match && parseFloat(match[1]) === parseFloat(product.size) && qty > 0;
          });
      } else {
        // If no size/length is entered, show all stock (or nothing)
        entries = [];
      }

      if (entries.length === 0) {
        // If we have a size/length entered but no stock, show "Out of Stock"
        if ((isReelOrTota && product.length) || (isRoll && product.size)) {
          return <span style={{ color: '#ff4444', fontWeight: '500', fontSize: '12px' }}>Out of Stock</span>;
        }
        // If no size/length entered, show nothing or a hint
        return null;
      }

      return entries.map(([display, qty]) => (
        <span key={display} style={{
          background: '#e8f5e9',
          padding: '1px 8px',
          borderRadius: '16px',
          fontSize: '11px',
          fontWeight: '500',
          color: '#1e7e34',
          whiteSpace: 'nowrap',
          border: '1px solid #a5d6a7',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px'
        }}>
          <span>{display}</span>
          <strong style={{ fontSize: '12px' }}>{qty}</strong>
        </span>
      ));
    })()}
  </div>
)}
                      {isRoll && product.size && selectedProduct && selectedProduct.stockByWeight && (
                        <div style={{ fontSize: '10px', marginTop: '2px', color: '#0a8938' }}>
                          Available: {
                            Object.entries(selectedProduct.stockByWeight)
                              .filter(([key, qty]) => {
                                const match = key.match(/^(\d+(?:\.\d+)?)/);
                                return match && parseFloat(match[1]) === parseFloat(product.size) && qty > 0;
                              })
                              .map(([key, qty]) => `${key}: ${qty}`)
                              .join(', ') || 'No stock for this size'
                          }
                        </div>
                      )}
                    </td>
                  )}

                  {showPacketColumns && (
                    <>
                      <td>{isPacket ? <input type="number" value={product.ktae || ""} onChange={e => handleChange(idx, "ktae", e.target.value)} className="read-only-input" placeholder="Ktae" /> : <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />}</td>
                      <td>{isPacket ? <input type="number" value={product.gram || ""} onChange={e => handleChange(idx, "gram", e.target.value)} className="read-only-input" placeholder="Gram" /> : <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />}</td>
                    </>
                  )}

                  {showBoxColumns && (
                    <td>
                      {category === "box" ? (
                        <input type="text" value={product.boxSize || ""} onChange={e => handleChange(idx, "boxSize", e.target.value)} className="read-only-input" placeholder="Box Size" />
                      ) : (
                        <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />
                      )}
                    </td>
                  )}

                  {showParticularColumn && (
                    <td>
                      {(category !== 'roll' && category !== 'packet') ? (
                        <input type="text" value={product.particular || ""} onChange={e => handleChange(idx, "particular", e.target.value)} className="read-only-input" placeholder="Particular" />
                      ) : (
                        <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />
                      )}
                    </td>
                  )}
                  <td>
  <button
    onClick={() => removeRow(idx)}
    className="remove-row-btn"
    title="Remove this row"
  >
    ✕
  </button>
</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "20px", marginTop: "20px" }}>
        <div><label style={{ fontWeight: "bold" }}>Discount</label><input type="number" value={generalDiscount} onChange={e => setGeneralDiscount(parseFloat(e.target.value) || 0)} className="read-only-input" /></div>
        <div><label style={{ fontWeight: "bold" }}>Rent</label><input type="number" value={rent} onChange={e => setRent(parseFloat(e.target.value) || 0)} className="read-only-input" /></div>
        <div><label style={{ fontWeight: "bold" }}>GST % (Optional)</label><input type="number" value={gstPercentage} onChange={e => setGstPercentage(e.target.value)} placeholder="e.g., 18" className="read-only-input" step="0.01" min="0" max="100" /></div>
        <div><label style={{ fontWeight: "bold" }}>Pay Bill</label><input type="number" value={amountPaid} onChange={e => setAmountPaid(e.target.value)} className="read-only-input" /></div>
      </div>

      {gstPercentage && parseFloat(gstPercentage) > 0 && (
        <div style={{ marginTop: "10px", padding: "10px", backgroundColor: "#f8f9fa", borderRadius: "6px", border: "1px solid #e9ecef", fontSize: "14px" }}>
          <div style={{ display: "flex", gap: "20px", justifyContent: "center" }}>
            <span><strong>Subtotal:</strong> Rs {subtotalBeforeGst.toFixed(2)}</span>
            <span><strong>GST ({gstPercentage}%):</strong> Rs {gstAmount.toFixed(2)}</span>
            <span><strong>Total after GST:</strong> Rs {(subtotalBeforeGst + gstAmount).toFixed(2)}</span>
          </div>
        </div>
      )}

      <div style={{ marginTop: "20px" }}>
        <label style={{ fontWeight: "bold", display: "block", marginBottom: "5px" }}>Invoice Image (optional)</label>
        <div style={{ border: "2px dashed #0a8938", padding: "20px", borderRadius: "8px", textAlign: "center", cursor: "pointer", backgroundColor: "#f9f9f9", color: "#555", width: "100%", boxSizing: "border-box" }} onClick={() => document.getElementById("invoiceImage").click()}>
          {invoiceImage ? invoiceImage.name : "Click or Drag to Upload Invoice Image"}
          <input ref={fileInputRef} id="invoiceImage" type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
        </div>
      </div>

      <div style={{ marginTop: "20px" }}>
        <label style={{ fontWeight: "bold" }}>Description</label>
        <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Enter Description" className="read-only-input" />
      </div>

      {totalQuantity > 0 && (
        <div style={{ marginTop: "10px", textAlign: "right", fontSize: "16px", fontWeight: "500", color: "#0a8938" }}>
          Total Quantity: {totalQuantity} {products.some(p => {
            const cat = (p.category || "").toLowerCase();
            return cat === "reel" || cat === "tota";
          }) ? "kg" : "units"}
        </div>
      )}

      <div style={{ marginTop: "20px", textAlign: "right", fontSize: "20px", fontWeight: "bold", color: "#0c6303" }}>
        Amount Payable: Rs {grandTotal}
        {gstPercentage && parseFloat(gstPercentage) > 0 && (
          <div style={{ fontSize: "14px", color: "#666", marginTop: "5px" }}>(Includes Rs {gstAmount.toFixed(2)} GST @ {gstPercentage}%)</div>
        )}
      </div>

      <div className="table-actions">
        <button onClick={removeAllRows} className="remove-btn">Remove All</button>
        <button onClick={handleSubmit} className="save-btn" disabled={isSubmitting} style={{ opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
          {isSubmitting ? 'Saving...' : 'Save All'}
        </button>
        <button onClick={addRow} className="add-btn">Add Product</button>
        <button onClick={handlePrintClick} className="print-btn" disabled={!canPrint}>Print</button>
      </div>

      <div style={{ position: "absolute", top: "-9999px" }}>
        <div className="print-mode">
          <UniversalInvoicePrint ref={printRef} invoice={printInvoice} type="purchase" />
        </div>
      </div>
    </div>
  );
}

export default Purchases;